import { useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import { DEFAULT_RULE, type Aircraft } from '@/lib/reference'
import {
  exceptionIndex,
  fleetColumnKey,
  resolveAirport,
  resolveCityPair,
  resolveCountry,
  substitutionPool,
  tailBlockedAtAirport,
  tailBlockedInCountry,
  type Verdict,
} from '@/lib/reference/fleet'
import { airportGroupIndex, countryGroupIndex } from '@/lib/reference/groups'
import { ReferenceTable, type Column } from '@/components/reference/ReferenceTable'

/**
 * The fleet, with the four limits that decide what each tail may work.
 *
 * All four are ceilings or denials: at most N cycles a day, at most N minutes
 * of block time on a leg, and two lists of places the tail may not go.
 *
 * Group, category and type together are the dimension every permission matrix
 * is keyed by, so editing any of the three here moves the tail to a different
 * column over on Capability.
 */
export function AircraftPage() {
  const { can } = useAuth()
  const {
    aircraft,
    countries,
    cityPairs,
    cityPairCapability,
    countryCapability,
    airportCapability,
    capabilityException,
    airportGroups,
    countryGroups,
    airports,
    create,
    update,
    remove,
  } = useReference()
  const [selected, setSelected] = useState<string | null>(null)

  const distinct = (pick: (row: Aircraft) => string) =>
    [...new Set(aircraft.map(pick).filter(Boolean))].sort()

  const groups = useMemo(() => distinct((row) => row.group), [aircraft])
  const categories = useMemo(() => distinct((row) => row.category), [aircraft])
  const types = useMemo(() => distinct((row) => row.type), [aircraft])

  const columns = useMemo<Column<Aircraft>[]>(
    () => [
      {
        key: 'regn',
        header: 'Regn',
        value: (row) => row.regn,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-[92px]',
        render: (row) => <span className="t-number-sm text-ink">{row.regn}</span>,
      },
      {
        key: 'group',
        header: 'Group',
        value: (row) => row.group,
        filter: 'select',
        editor: 'select',
        options: groups,
        cellClassName: 'w-[76px]',
      },
      {
        key: 'category',
        header: 'Cat',
        value: (row) => row.category,
        filter: 'select',
        editor: 'select',
        options: categories,
        cellClassName: 'w-[68px]',
      },
      {
        key: 'type',
        header: 'Type',
        value: (row) => row.type,
        filter: 'select',
        editor: 'select',
        options: types,
        cellClassName: 'w-[110px]',
      },
      {
        key: 'seatConfig',
        header: 'Seats',
        value: (row) => row.seatConfig,
        editor: 'text',
        cellClassName: 'w-[112px]',
        render: (row) => (
          <span className="t-number-sm whitespace-nowrap text-ink">
            {row.seatConfig} <span className="text-muted">{row.seats}</span>
          </span>
        ),
      },
      {
        key: 'maxCyclesPerDay',
        header: 'Max FC',
        value: (row) => row.maxCyclesPerDay,
        render: (row) =>
          row.maxCyclesPerDay ? row.maxCyclesPerDay : <span className="text-muted-soft">—</span>,
        editor: 'number',
        numeric: true,
        filter: 'none',
        cellClassName: 'w-[72px] text-right',
      },
      {
        key: 'maxBlockMinutes',
        header: 'Max block',
        value: (row) => row.maxBlockMinutes,
        render: (row) =>
          row.maxBlockMinutes ? (
            formatHours(row.maxBlockMinutes)
          ) : (
            <span className="text-muted-soft">—</span>
          ),
        editor: 'number',
        numeric: true,
        filter: 'none',
        cellClassName: 'w-[86px] text-right',
      },
      {
        key: 'limitCountry',
        header: 'No country',
        value: (row) => row.limitCountry.join(' '),
        render: (row) =>
          row.limitCountry.length ? (
            <span className="t-number-sm text-semantic-down">{row.limitCountry.join(' ')}</span>
          ) : (
            <span className="text-muted-soft">—</span>
          ),
        edit: (row) => row.limitCountry.join(','),
        editor: 'text',
        cellClassName: 'w-[104px]',
      },
      {
        key: 'limitAirport',
        header: 'No airport',
        value: (row) => row.limitAirport.join(' '),
        render: (row) =>
          row.limitAirport.length ? (
            <span className="t-number-sm text-semantic-down">{row.limitAirport.join(' ')}</span>
          ) : (
            <span className="text-muted-soft">—</span>
          ),
        edit: (row) => row.limitAirport.join(','),
        editor: 'text',
        cellClassName: 'w-[104px]',
      },
      {
        key: 'rank',
        header: 'Rank',
        value: (row) => row.rank,
        filter: 'select',
        options: [...new Set(aircraft.map((row) => String(row.rank)))].sort(),
        editor: 'number',
        numeric: true,
        cellClassName: 'w-[68px] text-right',
      },
      {
        // No column of its own: the notes run to a full sentence each and
        // would push the table into a horizontal scroll. Searchable, editable,
        // and shown in full in the panel.
        key: 'note',
        header: 'Note',
        value: (row) => row.note,
        editor: 'text',
        hidden: true,
      },
    ],
    [aircraft, groups, categories, types],
  )

  const tail = selected ? aircraft.find((row) => row.regn === selected) ?? null : null

  return (
    <section>
      <h1 className="t-title-lg text-ink">Aircraft</h1>

      {/* The panel column exists only while a panel does, and only once the
          window is wide enough to spare it — otherwise the table would give
          up width to nothing. */}
      <div
        className={`mt-6 grid gap-6 ${
          tail ? '2xl:grid-cols-[minmax(0,1fr)_360px]' : 'grid-cols-1'
        }`}
      >
        <ReferenceTable
          columns={columns}
          rows={aircraft}
          entity="Aircraft"
          canEdit={can('settings.edit')}
          // The seed arrives sorted by group then registration; no single
          // header can express that, so it is kept until the user sorts.
          defaultSortKey={null}
          selectedId={tail?.id ?? null}
          onSelect={(row) => setSelected((current) => (current === row.regn ? null : row.regn))}
          onCreate={(values) => create('aircraft', fieldsFrom(values))}
          onUpdate={(id, values) => update('aircraft', id, fieldsFrom(values))}
          onDelete={(id) => remove('aircraft', id)}
        />

        {tail && (
          <TailPanel
            tail={tail}
            fleet={aircraft}
            countryNames={new Map(countries.map((row) => [row.code, row.name] as const))}
            cityPairKeys={[DEFAULT_RULE, ...cityPairs.map((row) => row.key)]}
            cityPairCapability={cityPairCapability}
            countryCapability={countryCapability}
            airportCapability={airportCapability}
            capabilityException={capabilityException}
            airportGroupMembers={airportGroupIndex(airportGroups, airports, airportCapability)}
            countryGroupMembers={countryGroupIndex(countryGroups, countries, airports)}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </section>
  )
}

/** 120 -> "2h", 150 -> "2h30". */
function formatHours(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours}h${String(rest).padStart(2, '0')}` : `${hours}h`
}

/** Form values are strings; the two limit lists arrive comma-separated. */
function fieldsFrom(values: Record<string, string>) {
  const list = (raw: string) =>
    raw
      .split(',')
      .map((part) => part.trim().toUpperCase())
      .filter(Boolean)

  const cabins = parseCabins(values.seatConfig)
  return {
    regn: values.regn.trim().toUpperCase(),
    group: values.group.trim().toUpperCase(),
    category: values.category.trim().toUpperCase(),
    type: values.type.trim(),
    seatConfig: values.seatConfig.trim().toUpperCase(),
    cabins,
    seats: Object.values(cabins).reduce((sum, count) => sum + count, 0),
    maxCyclesPerDay: Number(values.maxCyclesPerDay) || 0,
    maxBlockMinutes: Number(values.maxBlockMinutes) || 0,
    limitCountry: list(values.limitCountry ?? ''),
    limitAirport: list(values.limitAirport ?? ''),
    rank: Number(values.rank) || 0,
    note: values.note?.trim() ?? '',
  }
}

/** Same reading as tools/build-reference-data.py: "Y240", "12C365Y". */
function parseCabins(text: string): Record<string, number> {
  const cabins: Record<string, number> = {}
  for (const match of text.toUpperCase().matchAll(/(\d+)([A-Z])|([A-Z])(\d+)/g)) {
    const [, digitsFirst, cabinAfter, cabinFirst, digitsAfter] = match
    if (cabinAfter) cabins[cabinAfter] = Number(digitsFirst)
    else if (cabinFirst) cabins[cabinFirst] = Number(digitsAfter)
  }
  return cabins
}

function TailPanel({
  tail,
  fleet,
  countryNames,
  cityPairKeys,
  cityPairCapability,
  countryCapability,
  airportCapability,
  capabilityException,
  airportGroupMembers,
  countryGroupMembers,
  onClose,
}: {
  tail: Aircraft
  fleet: Aircraft[]
  countryNames: Map<string, string>
  cityPairKeys: string[]
  cityPairCapability: ReturnType<typeof useReference>['cityPairCapability']
  countryCapability: ReturnType<typeof useReference>['countryCapability']
  airportCapability: ReturnType<typeof useReference>['airportCapability']
  capabilityException: ReturnType<typeof useReference>['capabilityException']
  airportGroupMembers: Map<string, Set<string>>
  countryGroupMembers: Map<string, Set<string>>
  onClose: () => void
}) {
  const pool = substitutionPool(tail, fleet)
  const poolByRank = new Map<number, number>()
  for (const candidate of pool) {
    poolByRank.set(candidate.rank, (poolByRank.get(candidate.rank) ?? 0) + 1)
  }

  // This tail's own column in every matrix: the trio, not the type alone.
  const column = fleetColumnKey(tail)
  const pairExceptions = exceptionIndex(capabilityException, 'cityPair')
  const countryExceptions = exceptionIndex(capabilityException, 'country')
  const airportExceptions = exceptionIndex(capabilityException, 'airport')

  return (
    <aside className="rounded-[24px] border border-hairline p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="t-title-md truncate text-ink">{tail.regn}</h2>
          <p className="t-caption mt-1 text-muted">
            {tail.group} · {tail.category} · {tail.type}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="rounded-[8px] p-1.5 text-body transition-colors hover:bg-surface-strong"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <dl className="mt-6 space-y-3">
        <Row label="Seats">
          <span className="t-number-sm">
            {tail.seatConfig} · {tail.seats}
          </span>
        </Row>
        <Row label="Rank">
          <span className="t-number-sm">{tail.rank}</span>
        </Row>
      </dl>

      <Section title="Limits">
        <dl className="space-y-3">
          <Row label="Max cycles / day">
            {tail.maxCyclesPerDay ? (
              <span className="t-number-sm">{tail.maxCyclesPerDay}</span>
            ) : (
              <span className="text-muted-soft">None</span>
            )}
          </Row>
          <Row label="Max block time">
            {tail.maxBlockMinutes ? (
              <span className="t-number-sm">{tail.maxBlockMinutes} min</span>
            ) : (
              <span className="text-muted-soft">None</span>
            )}
          </Row>
          <Row label="Country barred">
            {tail.limitCountry.length ? (
              <span className="t-number-sm text-semantic-down">{tail.limitCountry.join(' ')}</span>
            ) : (
              <span className="text-muted-soft">None</span>
            )}
          </Row>
          <Row label="Airport barred">
            {tail.limitAirport.length ? (
              <span className="t-number-sm text-semantic-down">{tail.limitAirport.join(' ')}</span>
            ) : (
              <span className="text-muted-soft">None</span>
            )}
          </Row>
        </dl>
      </Section>

      <Section title="City pairs">
        <ul className="space-y-2">
          {cityPairKeys.map((key) => (
            <li key={key} className="flex items-baseline justify-between gap-3">
              <span className="t-body-sm truncate text-body">{key}</span>
              <StateGlyph
                verdict={resolveCityPair(cityPairCapability, pairExceptions.get(key), key, column)}
              />
            </li>
          ))}
        </ul>
      </Section>

      {countryCapability.length > 0 && (
        <Section title="Countries with a rule">
          <ul className="space-y-2">
            {countryCapability.map((rule) => {
              // A group row is its own subject, so it reads its own cell; a
              // country row resolves through the layers.
              const verdict: Verdict =
                rule.subjectKind === 'group'
                  ? { state: rule.allow[column] ?? 'unset', source: 'rule' }
                  : resolveCountry(
                      countryCapability,
                      countryExceptions.get(rule.subject),
                      rule.subject,
                      column,
                      countryGroupMembers,
                    )
              // The tail tier has to expand groups, not compare strings: a
              // wet-leased tail carries one limit, NON-VN, and that is what
              // bars it from India as much as from anywhere else abroad.
              const barred =
                rule.subjectKind === 'group'
                  ? tail.limitCountry.includes(rule.subject)
                  : tailBlockedInCountry(tail, rule.subject, countryGroupMembers) !== null
              return (
                <li key={rule.id} className="flex items-baseline justify-between gap-3">
                  <span className="t-body-sm truncate text-body">
                    <span className="t-number-sm text-ink">{rule.subject}</span>{' '}
                    {rule.subjectKind === 'group'
                      ? 'group'
                      : (countryNames.get(rule.subject) ?? '')}
                  </span>
                  {/* Tail tier overrides the aircraft tier, never the other way. */}
                  <StateGlyph
                    verdict={barred ? { state: 'no', source: 'rule' } : verdict}
                    tailLimit={barred}
                  />
                </li>
              )
            })}
          </ul>
        </Section>
      )}

      {airportCapability.length > 0 && (
        <Section title="Airports with a rule">
          <ul className="space-y-2">
            {airportCapability.map((rule) => {
              const verdict: Verdict =
                rule.subjectKind === 'group'
                  ? { state: rule.allow[column] ?? 'unset', source: 'rule' }
                  : resolveAirport(
                      airportCapability,
                      airportExceptions.get(rule.subject),
                      rule.subject,
                      column,
                      airportGroupMembers,
                    )
              const barred =
                rule.subjectKind === 'group'
                  ? tail.limitAirport.includes(rule.subject)
                  : tailBlockedAtAirport(tail, rule.subject, airportGroupMembers) !== null
              return (
                <li key={rule.id} className="flex items-baseline justify-between gap-3">
                  <span className="t-number-sm truncate text-ink">
                    {rule.subject}
                    {rule.subjectKind === 'group' && (
                      <span className="t-caption text-muted"> group</span>
                    )}
                  </span>
                  <StateGlyph
                    verdict={barred ? { state: 'no', source: 'rule' } : verdict}
                    tailLimit={barred}
                  />
                </li>
              )
            })}
          </ul>
        </Section>
      )}

      <Section title="Substitution pool">
        <p className="t-body-sm text-body">
          <span className="t-number-sm text-ink">{pool.length}</span> tail
          {pool.length === 1 ? '' : 's'} at rank {tail.rank} or better
        </p>
        <ul className="mt-3 space-y-1.5">
          {[...poolByRank.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([rank, count]) => (
              <li key={rank} className="flex items-baseline justify-between">
                <span className="t-caption text-muted">Rank {rank}</span>
                <span className="t-number-sm text-ink">{count}</span>
              </li>
            ))}
        </ul>
      </Section>

      {tail.note && (
        <Section title="Note">
          {/* Operator's own wording about their own fleet, kept verbatim. */}
          <p className="t-body-sm whitespace-pre-line text-body">{tail.note}</p>
        </Section>
      )}
    </aside>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-8 border-t border-hairline-soft pt-5">
      <h3 className="t-title-sm mb-4 text-ink">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="t-caption text-muted">{label}</dt>
      <dd className="t-body-sm text-ink">{children}</dd>
    </div>
  )
}

/**
 * One verdict, with the layer that decided it.
 *
 * `Exception` and `Tail` are worth saying out loud: the first means the
 * standing rule says something else, the second means this tail is barred
 * where its trio is not.
 */
function StateGlyph({ verdict, tailLimit }: { verdict: Verdict; tailLimit?: boolean }) {
  const glyph = verdict.state === 'yes' ? '✓' : verdict.state === 'no' ? '✕' : '—'
  const tone =
    verdict.state === 'yes'
      ? 'text-semantic-up'
      : verdict.state === 'no'
        ? 'text-semantic-down'
        : 'text-muted-soft'
  const note = tailLimit ? 'Tail' : verdict.source === 'exception' ? 'Exception' : null

  return (
    <span className="flex items-baseline gap-1.5 whitespace-nowrap">
      {note && (
        <span className="t-caption-strong rounded-[100px] bg-ink px-2 py-0.5 text-on-dark">
          {note}
        </span>
      )}
      <span className={`t-title-sm ${tone}`} title={verdict.state === 'unset' ? 'Not set' : undefined}>
        {glyph}
      </span>
    </span>
  )
}
